import React, { useCallback, useEffect, useMemo } from 'react';
import {
  OfferList,
  Button,
  FAQItem,
  useIsSpecialOfferPageActive,
} from 'components';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Offer, OfferType, useOffers } from '@app/api';
import { SpecialOfferInfo } from './SpecialOfferInfo';
import { useNavigate } from 'react-router-dom';
import { G_CALENDAR_LINK } from 'components/GoogleCalendar/gCalendar';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    marginInline: '8vw',
    padding: [[theme.marginBase * 7, theme.marginBase * 12]],
    minHeight: '100vh',
    [theme.media.mobile]: {
      padding: [[theme.marginBase * 8, theme.marginBase * 2]],
      minHeight: 'auto',
    },

    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.marginBase * 8,
  },
  h1: {
    ...theme.fonts.h2,
    marginTop: theme.marginBase * 3,
    textAlign: 'center',
  },
  subTitle: {
    ...theme.fonts.h4,
    textAlign: 'center',
    marginTop: theme.marginBase * 3,
  },
  head: {},
  h2: {
    ...theme.fonts.h4,
    textAlign: 'center',
  },
  bannerContainer: {
    ...theme.fonts.h5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.marginBase * 3,
    padding: theme.marginBase * 4,
    backgroundColor: theme.colors.blueMuted,
    borderRadius: theme.borderRadius.std,
    width: '100%',
  },

  faqContainer: {
    backgroundColor: theme.colors.white,
    padding: theme.marginBase * 3,
    borderRadius: theme.borderRadius.std,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  faqTitle: {
    ...theme.fonts.h4,
    color: theme.colors.inkPrimary,
  },
}));

export const SpecialOfferPage = () => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const navigate = useNavigate();
  const isSpecialOfferActive = useIsSpecialOfferPageActive();

  useEffect(() => {
    if (!isSpecialOfferActive) {
      navigate('/');
    }
  }, [navigate]);

  const { data: offers } = useOffers();
  const creditOffers = useMemo(() => {
    if (!offers) return [];
    return offers
      .filter((offer) => offer.type === OfferType.SPECIAL_OFFER)
      .sort((a, b) => {
        if (a.prices.eur === 0) return 1;
        if (b.prices.eur === 0) return -1;
        return a.prices.eur - b.prices.eur;
      });
  }, [offers]);

  const onOfferSelect = useCallback(
    (offer: Offer) => navigate(`/payment?offer=${offer.id}`),
    [navigate],
  );

  if (!isSpecialOfferActive) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <h1 className={classes.h1}>{t('billing.specialOffer.title')}</h1>
        <p className={classes.subTitle}>{t('billing.specialOffer.subTitle')}</p>
      </div>
      <SpecialOfferInfo />
      <OfferList offers={creditOffers} onClick={onOfferSelect} />

      <div className={classes.bannerContainer}>
        {t('billing.specialOffer.needHelpBanner.text')}
        <Button
          href={G_CALENDAR_LINK}
          target="_blank"
          text={t('billing.specialOffer.needHelpBanner.button')}
        />
      </div>

      <div className={classes.faqContainer}>
        <h4 className={classes.faqTitle}>{t('billing.emptyPage.faq.title')}</h4>
        <FAQItem
          question={t(`billing.emptyPage.faq.items.validity.question`)}
          answer={t(`billing.emptyPage.faq.items.validity.answer`)}
        />
        <FAQItem
          question={t(`billing.emptyPage.faq.items.credits.question`)}
          answer={t(`billing.emptyPage.faq.items.credits.answer`)}
        />
        <FAQItem
          question={t(`billing.emptyPage.faq.items.creditPayment.question`)}
          answer={t(`billing.emptyPage.faq.items.creditPayment.answer`)}
        />
      </div>
    </div>
  );
};
